import { z } from 'zod'

import {
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  noteSchema,
  searchQuerySchema,
  sortSchema,
  versionSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * A48 · Parties — sellers, buyers, dalals, vendors, partners.
 *
 * The facets are **booleans, not a type**: the same man sells a bull in June
 * and buys one in August, and a `type` column would force the trader to enter
 * him twice and then wonder why the ledger shows two of him.
 *
 * This is the typeahead behind every party field in the app, which is why the
 * list is deliberately cheap and the search is one `q` over name and phone.
 */
export const partySchema = z.object({
  id: idSchema,
  name: z.string(),
  phone: z.string().nullish(),
  city: z.string().nullish(),
  isSeller: z.boolean(),
  isBuyer: z.boolean(),
  isDalal: z.boolean(),
  isVendor: z.boolean(),
  isPartner: z.boolean(),
  note: noteSchema.nullish(),
  /** Cheap relationship facts the picker shows under the name. */
  lastDealtOn: isoDateSchema.nullish(),
  outstandingPaisa: moneyPaisa(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

export const PARTY_SORTS = ['name', '-lastDealtOn', '-outstanding'] as const

export const partyListQuery = cursorQuerySchema.extend({
  q: searchQuerySchema.optional(),
  /** Filter the typeahead to the facet the field is asking for. */
  facet: z.enum(['seller', 'buyer', 'dalal', 'vendor', 'partner']).optional(),
  sort: sortSchema(PARTY_SORTS, 'name'),
})

export const createPartyRequest = z.object({
  name: z.string().trim().min(2).max(120),
  phone: z.string().trim().max(30).optional(),
  city: z.string().trim().max(80).optional(),
  isSeller: z.boolean().optional(),
  isBuyer: z.boolean().optional(),
  isDalal: z.boolean().optional(),
  isVendor: z.boolean().optional(),
  isPartner: z.boolean().optional(),
  note: noteSchema.optional(),
})

export const patchPartyRequest = createPartyRequest
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export const partyListResponse = z.array(partySchema)

export type Party = z.infer<typeof partySchema>
export type PartyListQuery = z.input<typeof partyListQuery>
export type CreatePartyRequest = z.input<typeof createPartyRequest>
export type PatchPartyRequest = z.input<typeof patchPartyRequest>
